import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LoginForm } from "../components/auth/LoginForm";
import { SecurityVerification } from "../components/auth/SecurityVerification";
import { ErrorMessage } from "../components/ui/ErrorMessage";
import { http, HttpError } from "../services/http";
import { API_ENDPOINTS } from "../config/api.config";
import { AdminLoginResponse } from "../domain/types/admin.types";
import { LoginSchemaType } from "../domain/validation/login.schema";
import { isSuperAdmin, saveTokens } from "../utils/auth";

type Step = "credentials" | "mfa";

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("credentials");
  const [error, setError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(false);

  const handleLogin = async (data: LoginSchemaType) => {
    setError("");
    setLoginLoading(true);
    try {
      const res = await http.post<AdminLoginResponse>(API_ENDPOINTS.auth.login, {
        email: data.email,
        password: data.password,
      });

      if (res.preAuthToken) {
        sessionStorage.setItem("adminPreAuthToken", res.preAuthToken);
        setStep("mfa");
        return;
      }

      if (res.accessToken && res.refreshToken) {
        saveTokens(res.accessToken, res.refreshToken);
        if (!isSuperAdmin()) {
          localStorage.removeItem("adminAuthToken");
          localStorage.removeItem("adminRefreshToken");
          setError("Access denied. Contact your admin for details.");
          return;
        }
        navigate("/");
      }
    } catch (err) {
      setError(err instanceof HttpError ? err.message : "Login failed. Please try again.");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleRequestCode = async (method: "sms" | "email") => {
    const preAuthToken = sessionStorage.getItem("adminPreAuthToken");
    const endpoint = method === "sms" ? API_ENDPOINTS.auth.resendPhone : API_ENDPOINTS.auth.resendEmail;
    await http.post(endpoint, undefined, {
      headers: { Authorization: `Bearer ${preAuthToken}` },
    });
  };

  const handleVerify = async (_method: "sms" | "authenticator" | "email", _code?: string) => {
    // Individual verify not used — handleConfirm handles submission
  };

  const handleConfirm = async (smsCode: string, emailCode: string, authenticatorCode: string) => {
    setError("");
    setMfaLoading(true);
    const preAuthToken = sessionStorage.getItem("adminPreAuthToken");
    try {
      const res = await http.post<AdminLoginResponse>(
        API_ENDPOINTS.auth.mfaVerify,
        {
          phoneCode: smsCode || undefined,
          emailCode: emailCode || undefined,
          authenticatorCode: authenticatorCode || undefined,
        },
        { headers: { Authorization: `Bearer ${preAuthToken}` } }
      );

      if (res.accessToken && res.refreshToken) {
        saveTokens(res.accessToken, res.refreshToken);
        sessionStorage.removeItem("adminPreAuthToken");
        if (!isSuperAdmin()) {
          localStorage.removeItem("adminAuthToken");
          localStorage.removeItem("adminRefreshToken");
          setError("Access denied. Contact your admin for details.");
          setStep("credentials");
          return;
        }
        navigate("/");
      } else {
        setError("Invalid verification codes. Please try again.");
      }
    } catch (err) {
      setError(err instanceof HttpError ? err.message : "Verification failed. Please try again.");
    } finally {
      setMfaLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 font-arial bg-primary-blue">
      <div>
        {error && (
          <ErrorMessage message={error} className="max-w-md mb-4" onDismiss={() => setError("")} />
        )}

        {step === "credentials" && (
          <LoginForm onSubmit={handleLogin} isLoading={loginLoading} />
        )}

        {step === "mfa" && (
          <SecurityVerification
            phone=""
            email=""
            googleAuthEnabled={true}
            onVerify={handleVerify}
            onRequestCode={handleRequestCode}
            onConfirm={handleConfirm}
            onBack={() => { setStep("credentials"); setError(""); }}
            isLoading={mfaLoading}
          />
        )}
      </div>
    </div>
  );
};
